import {
  booleanAttribute,
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatPseudoCheckbox } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { AccountService } from '@core/auth';
import { Scheme, Theming } from '@core/theming';

@Component({
  selector: 'user',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatPseudoCheckbox,
    RouterLink,
    TranslocoDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let user = account.profile();

    @if (compact()) {
      <button
        matButton
        [matMenuTriggerFor]="userMenu"
        class="tiny tertiary [--button-padding-x:0.25rem] [--button-padding-y:0.25rem]"
      >
        <span
          class="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary-3 text-xs font-semibold text-primary-11"
        >
          {{ user?.initials ?? '?' }}
        </span>
      </button>
    } @else {
      <button
        matButton
        [matMenuTriggerFor]="userMenu"
        class="tertiary small w-full [--button-padding-x:0.375rem] [--button-padding-y:0.375rem]"
      >
        <span class="mr-2 flex min-w-0 flex-auto items-center gap-x-3">
          <span
            class="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary-3 text-sm font-semibold text-primary-11"
          >
            {{ user?.initials ?? '?' }}
          </span>
          <span class="min-w-0 text-start">
            <span class="block truncate font-semibold">{{
              user?.fullName
            }}</span>
            <span class="block truncate text-sm">{{ user?.email ?? '' }}</span>
          </span>
        </span>

        <mat-icon
          class="mr-1 ml-auto"
          svgIcon="ellipsis-vertical"
          iconPositionEnd
        />
      </button>
    }

    <!-- User Menu -->
    <mat-menu
      class="min-w-52"
      xPosition="before"
      #userMenu
    >
      <button mat-menu-item>
        <span
          class="flex size-9 items-center justify-center rounded-lg bg-primary-3 text-sm font-semibold text-primary-11"
        >
          {{ user?.initials ?? '?' }}
        </span>
        <div class="ml-3 flex min-w-0 flex-auto flex-col select-none">
          <div class="truncate text-lg font-medium">
            {{ user?.fullName ?? '' }}
          </div>
          <div class="truncate text-sm text-neutral-a11">
            {{ user?.email ?? '' }}
          </div>
        </div>
      </button>

      <mat-divider />

      <a
        *transloco="let t"
        mat-menu-item
        routerLink="/settings/notifications"
      >
        <mat-icon svgIcon="bell" />
        <span>{{ t('SETTINGS.TABS.NOTIFICATIONS') }}</span>
      </a>

      <mat-divider />

      <button
        mat-menu-item
        [matMenuTriggerFor]="schemeMenu"
      >
        <mat-icon svgIcon="sun-moon" />
        <span>Color scheme</span>
      </button>
      <mat-menu #schemeMenu>
        @for (item of schemes; track item.value) {
          <button
            mat-menu-item
            (pointerdown)="updateScheme(item.value)"
          >
            <mat-pseudo-checkbox
              appearance="minimal"
              [state]="
                theming.scheme() === item.value ? 'checked' : 'unchecked'
              "
            />
            <span class="ml-1">{{ item.label }}</span>
          </button>
        }
      </mat-menu>

      <mat-divider />

      <button
        *transloco="let t"
        mat-menu-item
        (pointerdown)="signOut()"
      >
        <mat-icon svgIcon="log-out" />
        <span>{{ t('SENTENCES.SIGN_OUT') }}</span>
      </button>
    </mat-menu>
  `,
})
export class User {
  // Dependencies
  protected account = inject(AccountService);
  protected theming = inject(Theming);

  // Inputs / Outputs
  readonly compact = input(false, { transform: booleanAttribute });

  // State
  schemes: { label: string; value: Scheme }[] = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'System', value: 'system' },
  ];

  updateScheme(scheme: Scheme) {
    this.theming.scheme.set(scheme);
  }

  signOut() {
    this.account.logout();
  }
}
